import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  Heart,
  UserRound,
  Search,
  Crown,
  CheckCircle2,
  ChevronRight,
  Sparkles,
  Eye,
  MapPin,
  Briefcase,
  Send,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import Navbar from "../Landing/Navbar";
import { db } from "../firebase";
import useAuthAccess from "../hooks/useAuthAccess";
import { getProfileImages } from "../utils/profileLookup";
import { isPremiumActive } from "../utils/membership";
import { compareProfilesByRecency } from "../utils/profileSort";
import {
  isPendingVerification,
  isRejectedProfile,
  isVerifiedProfile,
  needsProfileChanges,
} from "../utils/verification";
import { isMarriageSettledProfile, canDisplayProfile } from "../utils/profileVisibility";
import { createProfilePath } from "../utils/profileRoutes";
import { getProfileDisplayImage, shouldShowProfilePlaceholder } from "../utils/profilePlaceholder";
import { DashboardHeroSkeleton, StatsRowSkeleton } from "../Components/Skeleton";
import BannerCarousel from "../Components/BannerCarousel";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

function getOppositeGender(g) {
  if (g === "Male") return "Female";
  if (g === "Female") return "Male";
  return "";
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function isRecentProfileSubmit(uid) {
  try {
    const raw = sessionStorage.getItem("karankiProfileJustSubmitted");
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (uid && parsed?.uid !== uid) return false;
    const at = Number(parsed?.at || 0);
    return at > 0 && Date.now() - at < 15000;
  } catch {
    return false;
  }
}

function getAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
  return age > 0 && age < 120 ? age : null;
}

/* Compact preview card used in the horizontal carousels */
function MiniProfileCard({ profile }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const photo = getProfileDisplayImage(profile);
  const isPlaceholder = shouldShowProfilePlaceholder(profile);
  const age = getAge(profile.dateOfBirth);
  const premium = isPremiumActive(profile);
  return (
    <Link
      to={createProfilePath(profile)}
      className="group relative w-full overflow-hidden rounded-2xl border border-sand-200 bg-white shadow-soft transition hover:-translate-y-1 hover:shadow-elevated"
    >
      {isPlaceholder ? (
        <img
          src={photo}
          alt={profile.fullName || "Profile"}
          className="aspect-[3/4] w-full object-cover object-center transition-all duration-500 group-hover:scale-[1.04]"
        />
      ) : (
        <div className="relative aspect-[3/4] w-full">
          {!imgLoaded && (
            <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-sand-200 via-sand-100 to-sand-200 bg-[length:800px_100%]" />
          )}
          <img
            src={photo}
            alt={profile.fullName}
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            className={`aspect-[3/4] w-full object-cover object-center transition-all duration-500 group-hover:scale-[1.04] ${imgLoaded ? "opacity-100" : "opacity-0"}`}
          />
        </div>
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />

      {premium && (
        <span className="absolute left-2 top-2 inline-flex items-center gap-0.5 rounded-full bg-amber-400 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-900 shadow-soft">
          <Crown size={9} /> Premium
        </span>
      )}

      <div className="absolute inset-x-0 bottom-0 p-2.5 text-white">
        <p className="truncate text-[13px] font-bold leading-tight">
          {profile.fullName}{age ? `, ${age}` : ""}
        </p>
        <div className="mt-0.5 flex items-center gap-1 text-[10.5px] text-white/85">
          {profile.profession ? (
            <span className="inline-flex min-w-0 items-center gap-0.5 truncate">
              <Briefcase size={9} className="shrink-0" /> <span className="truncate">{profile.profession}</span>
            </span>
          ) : (profile.presentResidence || profile.state) ? (
            <span className="inline-flex min-w-0 items-center gap-0.5 truncate">
              <MapPin size={9} className="shrink-0" /> <span className="truncate">{profile.presentResidence || profile.state}</span>
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

const Dashboard = () => {
  const { user, profile, loading: authLoading } = useAuthAccess();
  const justSubmittedRecently = isRecentProfileSubmit(user?.uid);

  const profileId = profile?.id;
  const targetGender = getOppositeGender(profile?.gender);
  const premium = isPremiumActive(profile);

  const recommendationsQuery = useQuery({
    queryKey: ["dashboardRecommendations", profileId, targetGender, premium],
    queryFn: async () => {
      if (!profileId || !targetGender || !user) return [];

      const userDoc = await getDoc(doc(db, "users", user.uid));
      const blockedIds = userDoc.exists() ? userDoc.data().blocked || [] : [];

      const recsSnap = await getDocs(
        query(
          collection(db, "profiles"),
          where("gender", "==", targetGender),
          where("profileStatus", "==", "Verified"),
          orderBy("createdAt", "desc"),
          limit(24)
        )
      );

      const candidates = recsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => p.id !== user.uid && canDisplayProfile(p, profile) && !blockedIds.includes(p.id));

      return candidates.sort(compareProfilesByRecency).slice(0, 6);
    },
    enabled: !!profileId && !!targetGender && !!user,
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const countsQuery = useQuery({
    queryKey: ["dashboardCounts", user?.uid],
    queryFn: async () => {
      if (!user?.uid) {
        return { sent: 0, received: 0, accepted: 0, views: 0 };
      }

      const sentQuery = query(collection(db, "interests"), where("fromUserId", "==", user.uid));
      const recvQuery = query(collection(db, "interests"), where("toUserId", "==", user.uid));
      const acceptedSentQuery = query(
        collection(db, "interests"),
        where("fromUserId", "==", user.uid),
        where("status", "==", "accepted")
      );
      const acceptedRecvQuery = query(
        collection(db, "interests"),
        where("toUserId", "==", user.uid),
        where("status", "==", "accepted")
      );
      const viewsQuery = query(collection(db, "profileViews"), where("viewedId", "==", user.uid));

      const [sentSnap, recvSnap, acceptedSentSnap, acceptedRecvSnap, viewsSnap] = await Promise.all([
        getCountFromServer(sentQuery).catch(() => getDocs(sentQuery)),
        getCountFromServer(recvQuery).catch(() => getDocs(recvQuery)),
        getCountFromServer(acceptedSentQuery).catch(() => getDocs(acceptedSentQuery)),
        getCountFromServer(acceptedRecvQuery).catch(() => getDocs(acceptedRecvQuery)),
        getCountFromServer(viewsQuery).catch(() => getDocs(viewsQuery)),
      ]);

      const sentCount = sentSnap.data ? sentSnap.data().count || 0 : sentSnap.size;
      const recvCount = recvSnap.data ? recvSnap.data().count || 0 : recvSnap.size;
      const acceptedCount =
        (acceptedSentSnap.data ? acceptedSentSnap.data().count || 0 : acceptedSentSnap.size) +
        (acceptedRecvSnap.data ? acceptedRecvSnap.data().count || 0 : acceptedRecvSnap.size);
      const viewsCount = viewsSnap.data ? viewsSnap.data().count || 0 : viewsSnap.size;

      return {
        sent: sentCount,
        received: recvCount,
        accepted: acceptedCount,
        views: viewsCount,
      };
    },
    enabled: !!user?.uid,
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const loading = authLoading || recommendationsQuery.isLoading || countsQuery.isLoading;
  const recsLoading = recommendationsQuery.isLoading;
  const recommendations = recommendationsQuery.data || [];
  const interestCounts = countsQuery.data || { received: 0, sent: 0, accepted: 0, views: 0 };
  const viewsCount = countsQuery.data?.views || 0;

  const photo = getProfileImages(profile)[0];
  const verified = isVerifiedProfile(profile);

  const completion = profile?.profileCompletion || 0;

  useEffect(() => {
    if (profile?.id) {
      sessionStorage.removeItem("karankiProfileJustSubmitted");
    }
  }, [profile?.id]);

  const verificationNotice = useMemo(() => {
    if (!profile) return null;
    if (isVerifiedProfile(profile)) return null;
    if (isPendingVerification(profile))
      return { type: "pending" };
    if (needsProfileChanges(profile)) {
      const rawRemarks = profile.verificationRemarks || "";
      const items = rawRemarks.split(/\n|•|-/).map((s) => s.trim()).filter(Boolean);
      return {
        type: "changes",
        text: items.length <= 1 ? (rawRemarks || "") : "",
        items: items.length > 1 ? items : [],
      };
    }
    if (isRejectedProfile(profile))
      return {
        type: "rejected",
        text: profile.verificationRemarks || "Please update your profile and resubmit for verification.",
      };
    return null;
  }, [profile]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="page-container pt-4 pb-app-nav lg:pt-8">
          {/* Greeting skeleton */}
          <div className="flex items-center gap-3">
            <div className="skeleton h-14 w-14 rounded-2xl" />
            <div className="flex-1 space-y-2">
              <div className="skeleton h-3 w-20" />
              <div className="skeleton h-6 w-40" />
              <div className="flex gap-1.5">
                <div className="skeleton h-4 w-16 rounded-full" />
                <div className="skeleton h-4 w-14 rounded-full" />
              </div>
            </div>
          </div>
          <div className="skeleton mt-3 h-12 w-full rounded-2xl sm:hidden" />

          {/* Stats skeleton */}
          <div className="mt-5 grid grid-cols-4 gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card flex flex-col items-center gap-1.5 p-3">
                <div className="skeleton h-8 w-8 rounded-xl" />
                <div className="skeleton h-5 w-6" />
                <div className="skeleton h-3 w-12" />
              </div>
            ))}
          </div>

          {/* Recommendations skeleton */}
          <div className="mt-7">
            <div className="flex items-center gap-2">
              <div className="skeleton h-8 w-8 rounded-xl" />
              <div className="space-y-1">
                <div className="skeleton h-5 w-40" />
                <div className="skeleton h-3 w-28" />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="overflow-hidden rounded-2xl border border-sand-200">
                  <div className="skeleton aspect-[3/4] w-full rounded-none" />
                  <div className="p-2.5 space-y-1.5">
                    <div className="skeleton h-3.5 w-3/4" />
                    <div className="skeleton h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!profile) {
    if (justSubmittedRecently) {
      return (
        <div className="min-h-screen bg-white">
          <Navbar />
          <main className="page-container pt-4 pb-app-nav lg:pt-8">
            <div className="flex items-center gap-3">
              <div className="skeleton h-14 w-14 rounded-2xl" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-3 w-20" />
                <div className="skeleton h-6 w-40" />
              </div>
            </div>
            <div className="mt-6 rounded-2xl border border-sand-200 bg-white p-5 text-center text-[13px] text-ink-500 shadow-soft">
              Finalizing your dashboard...
            </div>
          </main>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="page-container flex min-h-[70vh] items-center justify-center pb-app-nav">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-elevated w-full max-w-md p-8 text-center"
          >
            <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
              <CheckCircle2 size={28} />
            </div>
            <h1 className="mt-5 font-display text-2xl font-bold text-ink-800">
              Create your profile
            </h1>
            <p className="mt-2 text-[14px] leading-7 text-ink-500">
              Complete your matrimony profile to start finding matches.
            </p>
            <Link to="/create-profile" className="btn-primary mt-6 px-6 py-3">
              Get started <ChevronRight size={16} />
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  if (isMarriageSettledProfile(profile)) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="page-container flex min-h-[70vh] flex-col items-center justify-center pb-app-nav text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md"
          >
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
              <Heart size={36} className="text-emerald-500" />
            </div>
            <h1 className="mt-6 font-display text-2xl font-bold text-ink-800">
              Congratulations! 🎉
            </h1>
            <p className="mt-3 text-[14px] leading-7 text-ink-500">
              Your marriage has been settled. We wish you a happy and blessed life together.
            </p>
            <p className="mt-2 text-[13px] text-ink-400">
              Your profile has been removed from active matches. Thank you for being part of Karanki Matrimony.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Link to="/settings" className="btn-ghost py-3 text-[13px]">
                Settings
              </Link>
              <Link to="/settings/payments" className="btn-primary py-3 text-[13px]">
                View receipts
              </Link>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  const stats = [
    { label: "Received", value: interestCounts.received, Icon: Heart, tint: "bg-rose-50 text-rose-600", to: "/interests" },
    { label: "Accepted", value: interestCounts.accepted, Icon: CheckCircle2, tint: "bg-emerald-50 text-emerald-600", to: "/interests" },
    { label: "Sent", value: interestCounts.sent, Icon: Send, tint: "bg-sky-50 text-sky-600", to: "/interests" },
    { label: "Profile views", value: viewsCount, Icon: Eye, tint: "bg-violet-50 text-violet-600", to: "/settings/views" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="page-container pt-4 pb-app-nav sm:pt-6 lg:pt-8">
        {/* HEADER — Simple greeting */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
        >
          <div className="flex items-center gap-3">
            <Link to="/my-profile" className="relative shrink-0">
              {photo ? (
                <img
                  src={photo}
                  alt={profile.fullName}
                  className="h-14 w-14 rounded-2xl object-cover object-center ring-2 ring-sand-200 shadow-soft"
                />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-soft">
                  <UserRound size={24} />
                </div>
              )}
              {premium && (
                <span className="absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-amber-900 shadow">
                  <Crown size={9} />
                </span>
              )}
            </Link>
            <div className="min-w-0 flex-1">
              <p className="text-[11.5px] font-semibold text-ink-400">{greeting()}</p>
              <h1 className="font-display text-xl font-bold text-ink-800">{profile.fullName}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {profile.kmProfileId && (
                  <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-bold text-brand-600">{profile.kmProfileId}</span>
                )}
                {verified && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                    <ShieldCheck size={9} /> Verified
                  </span>
                )}
                {premium && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                    <Crown size={9} /> Premium
                  </span>
                )}
              </div>
            </div>
            <Link to="/matches" className="hidden shrink-0 items-center gap-1.5 rounded-2xl bg-brand-gradient px-5 py-2.5 text-[13px] font-semibold text-white shadow-soft sm:inline-flex">
              <Search size={14} /> Find matches
            </Link>
          </div>
          <Link
            to="/matches"
            className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-2xl bg-brand-gradient py-3 text-[14px] font-semibold text-white shadow-soft sm:hidden"
          >
            <Search size={16} /> Find your matches
          </Link>
        </motion.div>

        {/* VERIFICATION NOTICE */}
        {verificationNotice && (
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="mt-3"
          >
            {verificationNotice.type === "pending" && (
              <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100">
                  <svg className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-bold text-amber-800">Profile under review</p>
                  <p className="text-[11.5px] text-amber-700">Our team is reviewing your profile.</p>
                </div>
                <span className="shrink-0 rounded-full bg-amber-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">Pending</span>
              </div>
            )}

            {verificationNotice.type === "changes" && (
              <div className="overflow-hidden rounded-2xl border border-[#f5c6d4] bg-[#fff5f8]">
                {/* Header row */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#c90243]/10">
                    <svg className="h-4 w-4 text-[#c90243]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-bold text-[#c90243]">Action required</p>
                    <p className="text-[11.5px] text-[#c90243]/70">
                      {verificationNotice.text || "Please update your profile"}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-[#c90243]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#c90243]">Update</span>
                </div>

                {/* Change items */}
                {verificationNotice.items && verificationNotice.items.length > 0 && (
                  <div className="border-t border-[#f5c6d4] px-4 py-3 space-y-2">
                    {verificationNotice.items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2.5">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#c90243] text-[9px] font-bold text-white">
                          {idx + 1}
                        </span>
                        <span className="text-[12.5px] font-medium text-ink-700">{item}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* CTA */}
                <div className="border-t border-[#f5c6d4] px-4 py-3">
                  <Link
                    to="/my-profile"
                    className="flex items-center justify-center gap-2 rounded-xl bg-[#c90243] py-2.5 text-[13px] font-semibold text-white transition hover:bg-[#a8003a]"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Update my profile
                    <ChevronRight size={13} />
                  </Link>
                </div>
              </div>
            )}

            {verificationNotice.type === "rejected" && (
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-200">
                    <svg className="h-4 w-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-bold text-slate-700">Verification rejected</p>
                    <p className="text-[11.5px] text-slate-500 line-clamp-1">{verificationNotice.text}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-slate-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">Rejected</span>
                </div>
                <div className="border-t border-slate-200 px-4 py-3">
                  <Link
                    to="/my-profile"
                    className="flex items-center justify-center gap-2 rounded-xl bg-slate-700 py-2.5 text-[13px] font-semibold text-white transition hover:bg-slate-800"
                  >
                    Update &amp; resubmit <ChevronRight size={13} />
                  </Link>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* ACTIVITY STATS */}
        {countsQuery.isLoading ? (
          <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card flex items-center gap-3 p-3.5">
                <div className="skeleton h-11 w-11 rounded-xl" />
                <div className="space-y-2">
                  <div className="skeleton h-5 w-8" />
                  <div className="skeleton h-3 w-14" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            {stats.map(({ label, value, Icon, tint, to }) => (
              <Link
                key={label}
                to={to}
                className="card flex items-center gap-3 p-3.5 transition hover:-translate-y-0.5 hover:shadow-elevated"
              >
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                  <Icon size={19} />
                </div>
                <div className="min-w-0">
                  <p className="font-display text-xl font-bold leading-none text-ink-800">{value}</p>
                  <p className="mt-1 text-[11px] font-semibold text-ink-400">{label}</p>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* RECOMMENDED MATCHES */}
        <section className="mt-7">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                  <Sparkles size={16} />
                </div>
                <div>
                  <h2 className="font-display text-lg font-bold leading-tight text-ink-800">Recent profiles</h2>
                  <p className="text-[12px] text-ink-400">Newest verified members</p>
                </div>
              </div>
              <Link to="/matches" className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-600 hover:underline">
                View all <ArrowRight size={13} />
              </Link>
            </div>

            {recsLoading ? (
              <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="overflow-hidden rounded-2xl border border-sand-200 bg-white">
                    <div className="skeleton aspect-[3/4] w-full rounded-none" />
                    <div className="p-2.5 space-y-1.5">
                      <div className="skeleton h-4 w-3/4" />
                      <div className="skeleton h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recommendations.length === 0 ? (
              <div className="mt-3 card flex flex-col items-center p-7 text-center">
                <Sparkles className="text-brand-600" />
                <p className="mt-2 text-[13px] font-semibold text-ink-600">No recommendations yet</p>
                <p className="mt-0.5 text-[12px] text-ink-400">New profiles are added every day. Check back soon.</p>
                <Link to="/matches" className="btn-outline mt-4 px-5 py-2 text-[12.5px]">Browse all profiles</Link>
              </div>
            ) : (
              <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
                {recommendations.map((rec) => (
                  <MiniProfileCard key={rec.id} profile={rec} />
                ))}
              </div>
            )}

            {/* View more matches link */}
            {!recsLoading && recommendations.length > 0 && (
              <Link
                to="/matches"
                className="mt-4 flex items-center justify-center gap-2 rounded-2xl border border-brand-100 bg-brand-50/50 py-3 text-[13.5px] font-semibold text-brand-600 transition hover:bg-brand-50"
              >
                View all matches <ChevronRight size={15} />
              </Link>
            )}

            {/* Ad Banners */}
            <BannerCarousel isPremium={premium} />
          </section>

        {/* MEMBERSHIP UPSELL */}
        {!premium && verified && (
          <motion.section
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="mt-7 overflow-hidden rounded-2xl border border-sand-200 bg-white"
          >
            <div className="p-4 sm:p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                  <Crown size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-[15px] font-bold leading-5 text-ink-800">Upgrade to Premium</h3>
                  <p className="mt-0.5 text-[11px] leading-4 text-ink-500">Unlock more ways to connect</p>
                </div>
                <Link
                  to="/settings/membership"
                  className="inline-flex min-h-9 shrink-0 items-center justify-center rounded-lg bg-brand-600 px-3 text-[11px] font-semibold text-white transition hover:bg-brand-700"
                >
                  View plans
                </Link>
              </div>

              <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-sand-100 pt-3">
                {[
                  "See who viewed your profile",
                  "Send unlimited interests",
                  "View contact numbers",
                  "Advanced search filters",
                ].map((benefit) => (
                  <li
                    key={benefit}
                    className="flex min-w-0 items-start gap-1.5"
                  >
                    <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span className="text-[10px] font-medium leading-4 text-ink-600 sm:text-[11px]">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.section>
        )}

        <div className="h-4" />
      </main>
    </div>
  );
};

export default Dashboard;
